// useApiSync.js — хук для синхронизации CRUD-операций Tab-компонентов с API
// Паттерн (текущий, Phase A 2026-05-26): optimistic update в UI (через uOrg в callsite)
// + fire-and-forget API call в фоне. При ошибке — ЯВНЫЙ alert юзеру.
// Phase B (TODO): callsites переписать на await+rollback — UI не показывает запись
// если backend не подтвердил. Outbox удалён (см. shared.js header).

import { useCallback, useRef } from 'react';
import * as api from './api';

// handleSyncError — alert юзеру при ЛЮБОЙ ошибке кроме 401 (auth retry автоматический).
// Phase A (2026-05-26): убрали silent для isOffline/no-status/5xx — юзер должен знать.
//
// История:
//   - До #1 (13 мая): silent .catch. "Магия исчезновения данных" — UI показывал, в БД нет.
//   - #1 (13 мая): alert на 4xx/5xx, но silent на offline/no-status. Юзер всё ещё мог
//     не знать что network fail = data loss.
//   - #54/#48 (25-26 мая): outbox с silent retry — узер не понимал что pending.
//   - Phase A (26 мая, ОТКАТ): outbox удалён, handleSyncError alert'ит ВСЁ.
//   - Phase B (TODO): callsites await + rollback uOrg при ошибке. UI consistency.
//
// Filtering policy:
//   - 401 Unauthorized → silent: apiFetch ловит и делает auto-refresh+retry / clearAuth+reload
//   - isOffline / status=0 → alert "Нет связи с сервером"
//   - 409 VERSION_CONFLICT → alert "обновите страницу"
//   - 4xx (400/403/404/422) → alert с сервер-message
//   - 5xx → alert "ошибка сервера"
function handleSyncError(action) {
  return (e) => {
    console.error(`[apiSync] ${action} failed:`, e?.status || '?', e?.code || '', e?.message || e);
    // 401 silent: apiFetch уже сделал auto-refresh или clearAuth+reload (USER_NOT_FOUND/TOKEN_INVALIDATED).
    if (e?.status === 401) return;
    // Phase A (26 мая): network/offline — алертим явно, не silent.
    if (e?.isOffline || !e?.status) {
      try {
        window.alert(
          `❌ Нет связи с сервером.\n\n` +
          `Действие "${action}" НЕ сохранено в системе.\n\n` +
          `Проверьте интернет-соединение и обновите страницу (F5). ` +
          `Возможно, операция отображается в UI, но в базу данных не записана — ` +
          `после обновления страницы она исчезнет и потребуется повторить.`
        );
      } catch {}
      return;
    }
    const status = e.status;
    if (status === 409) {
      if (e?.code === 'VERSION_CONFLICT') {
        window.alert(`Не удалось ${action}: данные были изменены в другой сессии. Обновите страницу (F5) и повторите.`);
      } else {
        window.alert(`Не удалось ${action}: ${e?.message || 'конфликт данных'}.`);
      }
      return;
    }
    if (status === 403) {
      window.alert(`Не удалось ${action}: недостаточно прав. Возможно сессия истекла или роль изменилась. Обновите страницу.`);
      return;
    }
    if (status >= 500) {
      window.alert(
        `❌ Ошибка сервера при попытке ${action}.\n\n` +
        `Действие НЕ сохранено. Обновите страницу (F5) и повторите.`
      );
      return;
    }
    if (status >= 400 && status < 500) {
      const msg = e?.message || 'некорректный запрос';
      window.alert(`Не удалось ${action}: ${msg}. Действие НЕ сохранено.`);
    }
  };
}

export default function useApiSync(orgId) {
  const orgIdRef = useRef(orgId);
  orgIdRef.current = orgId;

  // Task #50 (2026-05-25): helper — emit 'org-version-updated' event если backend
  // вернул _orgUpdatedAt. App.js listener → syncManager.setVersion → меньше 409 race
  // на следующем saveOrg. Симметрично с onSaved path для bulk PUT.
  const emitVersionUpdate = (result, orgId) => {
    const updatedAt = result?._orgUpdatedAt;
    if (updatedAt && orgId) {
      try {
        window.dispatchEvent(new CustomEvent('org-version-updated', {
          detail: { orgId, updatedAt },
        }));
      } catch {}
    }
  };

  // ═══ Операции (transactions) ═══
  // O1 (2026-06-09): возвращает Promise + result для sequential (O2). Аддитивно — старые
  // fire-and-forget вызовы игнорируют возврат, поведение не меняется.
  const syncCreateOperation = useCallback((opData) => {
    if (!api.getToken() || !orgIdRef.current) return Promise.resolve();
    const orgId = orgIdRef.current;
    return api.createOperation(orgId, opData)
      .then(result => { emitVersionUpdate(result, orgId); return result; })
      .catch(handleSyncError('создать операцию'));
  }, []);

  const syncUpdateOperation = useCallback((opId, opData) => {
    if (!api.getToken() || !orgIdRef.current) return Promise.resolve();
    const orgId = orgIdRef.current;
    return api.updateOperation(orgId, opId, opData)
      .then(result => { emitVersionUpdate(result, orgId); return result; })
      .catch(handleSyncError('обновить операцию'));
  }, []);

  const syncDeleteOperation = useCallback((opId) => {
    if (!api.getToken() || !orgIdRef.current) return Promise.resolve();
    const orgId = orgIdRef.current;
    return api.deleteOperation(orgId, opId)
      .then(result => { emitVersionUpdate(result, orgId); return result; })
      .catch(handleSyncError('удалить операцию'));
  }, []);

  // Stage 20g (2026-06-08): отмена оплаты долга — возвращает Promise для await перед refetch.
  const syncUndoDebtPayment = useCallback((opId) => {
    if (!api.getToken() || !orgIdRef.current) return Promise.resolve();
    const orgId = orgIdRef.current;
    return api.undoDebtPayment(orgId, opId)
      .then(result => { emitVersionUpdate(result, orgId); return result; })
      .catch(handleSyncError('отменить оплату долга'));
  }, []);

  // ═══ Categories (Stage 20b B.3, 2026-05-14) ═══
  const syncCreateCategory = useCallback((data) => {
    if (!api.getToken() || !orgIdRef.current) return Promise.resolve();
    return api.createCategory(orgIdRef.current, data)
    .then(() => { try { window.dispatchEvent(new CustomEvent('inmonex-refetch-categories')); } catch (e) {} return true; })
    .catch(handleSyncError('создать категорию'));
  }, []);

  const syncUpdateCategory = useCallback((catId, data) => {
    if (!api.getToken() || !orgIdRef.current) return Promise.resolve();
    return api.updateCategory(orgIdRef.current, catId, data)
    .then(() => { try { window.dispatchEvent(new CustomEvent('inmonex-refetch-categories')); } catch (e) {} return true; })
    .catch(handleSyncError('обновить категорию'));
  }, []);

  const syncArchiveCategory = useCallback((catId) => {
    if (!api.getToken() || !orgIdRef.current) return Promise.resolve();
    return api.archiveCategory(orgIdRef.current, catId)
    .then(() => { try { window.dispatchEvent(new CustomEvent('inmonex-refetch-categories')); } catch (e) {} return true; })
    .catch(handleSyncError('архивировать категорию'));
  }, []);

  // ═══ Счета (accounts) ═══
  const syncCreateAccount = useCallback((accData) => {
    if (!api.getToken() || !orgIdRef.current) return Promise.resolve();
    return api.createAccount(orgIdRef.current, accData)
      .then(result => result)
      .catch(handleSyncError('создать счёт'));
  }, []);

  const syncUpdateAccount = useCallback((accId, accData) => {
    if (!api.getToken() || !orgIdRef.current) return Promise.resolve();
    return api.updateAccount(orgIdRef.current, accId, accData)
      .then(result => result)
      .catch(handleSyncError('обновить счёт'));
  }, []);

  const syncDeleteAccount = useCallback((accId) => {
    if (!api.getToken() || !orgIdRef.current) return Promise.resolve();
    return api.deleteAccount(orgIdRef.current, accId).then(() => true).catch(handleSyncError('удалить счёт'));
  }, []);

  // Чанк J (2026-05-19): atomic archive счёта с переносом balance.
  // Возвращает Promise чтобы caller мог await перед обновлением UI.
  const syncArchiveAccountWithTransfer = useCallback((accId, payload) => {
    if (!api.getToken() || !orgIdRef.current) return Promise.resolve();
    return api.archiveAccountWithTransfer(orgIdRef.current, accId, payload)
      .catch(handleSyncError('архивировать счёт с переносом'));
  }, []);

  // ═══ Обязательства (debts) ═══
  // Task #52 (2026-05-25): syncCreateDebt возвращает Promise + throw для InvoiceImportModal.
  const syncCreateDebt = useCallback(async (debtData) => {
    if (!api.getToken() || !orgIdRef.current) return;
    try {
      return await api.createDebt(orgIdRef.current, debtData);
    } catch (e) {
      handleSyncError('создать обязательство')(e);
      throw e;
    }
  }, []);

  const syncUpdateDebt = useCallback((debtId, debtData) => {
    if (!api.getToken() || !orgIdRef.current) return Promise.resolve();
    return api.updateDebt(orgIdRef.current, debtId, debtData).then(() => true).catch(handleSyncError('обновить обязательство'));
  }, []);

  const syncDeleteDebt = useCallback((debtId) => {
    if (!api.getToken() || !orgIdRef.current) return Promise.resolve();
    return api.deleteDebt(orgIdRef.current, debtId).then(() => true).catch(handleSyncError('удалить обязательство'));
  }, []);

  // ═══ Контрагенты (contractors) ═══
  const syncCreateContractor = useCallback((data) => {
    if (!api.getToken() || !orgIdRef.current) return Promise.resolve();
    return api.createContractor(orgIdRef.current, data)
      .then(result => result)
      .catch(handleSyncError('создать контрагента'));
  }, []);

  const syncUpdateContractor = useCallback((id, data) => {
    if (!api.getToken() || !orgIdRef.current) return Promise.resolve();
    return api.updateContractor(orgIdRef.current, id, data).then(() => true).catch(handleSyncError('обновить контрагента'));
  }, []);

  const syncDeleteContractor = useCallback((id) => {
    if (!api.getToken() || !orgIdRef.current) return Promise.resolve();
    return api.deleteContractor(orgIdRef.current, id).then(() => true).catch(handleSyncError('удалить контрагента'));
  }, []);

  // ═══ Фонды (funds) — Stage 20a Phase 2 / Чанк 20a.4c.1 ═══
  // F1 (2026-06-06): функции возвращают Promise (как syncArchiveFundWithRedistribute ниже),
  // чтобы callsites могли await перед refetch/UI (sequential pattern, F2/F3). Возврат
  // аддитивен — старые fire-and-forget вызовы игнорируют его, поведение не меняется.
  // .catch(handleSyncError) обрабатывает ошибку (alert) и НЕ ре-throw'ит — поэтому
  // возвращённый Promise всегда resolve'ится (после успеха ИЛИ обработанной ошибки).
  const syncCreateFund = useCallback((fundData) => {
    if (!api.getToken() || !orgIdRef.current) return Promise.resolve();
    return api.createFund(orgIdRef.current, fundData).catch(handleSyncError('создать фонд'));
  }, []);

  const syncUpdateFund = useCallback((fundId, fundData) => {
    if (!api.getToken() || !orgIdRef.current) return Promise.resolve();
    return api.updateFund(orgIdRef.current, fundId, fundData).catch(handleSyncError('обновить фонд'));
  }, []);

  const syncArchiveFund = useCallback((fundId) => {
    if (!api.getToken() || !orgIdRef.current) return Promise.resolve();
    return api.archiveFund(orgIdRef.current, fundId).catch(handleSyncError('архивировать фонд'));
  }, []);

  // Чанк I (2026-05-19): atomic архивация с перераспределением balance + percent.
  // Возвращает Promise — caller может await чтобы избежать race с polling refresh.
  const syncArchiveFundWithRedistribute = useCallback((fundId, payload) => {
    if (!api.getToken() || !orgIdRef.current) return Promise.resolve();
    return api.archiveFundWithRedistribute(orgIdRef.current, fundId, payload)
      .catch(handleSyncError('архивировать фонд с перераспределением'));
  }, []);

  // ═══ Пользователи организации ═══
  const syncAddUser = useCallback((userData) => {
    if (!api.getToken() || !orgIdRef.current) return;
    api.addUserToOrg(orgIdRef.current, userData).catch(handleSyncError('добавить пользователя'));
  }, []);

  const syncUpdateUserRole = useCallback((userId, role, permissions) => {
    if (!api.getToken() || !orgIdRef.current) return;
    api.updateUserRole(orgIdRef.current, userId, role, permissions).catch(handleSyncError('обновить роль пользователя'));
  }, []);

  const syncRemoveUser = useCallback((userId) => {
    if (!api.getToken() || !orgIdRef.current) return;
    api.removeUser(orgIdRef.current, userId).catch(handleSyncError('удалить пользователя'));
  }, []);

  // Stage 21.5 (2026-05-14): audit entries — frontend addAuditLog параллельно
  // с uOrg вызывает это для немедленной персистенции в AuditLog таблицу.
  const syncCreateAuditEntry = useCallback((entry) => {
    if (!api.getToken() || !orgIdRef.current) return;
    if (!entry || typeof entry !== 'object') return;
    api.createAuditLog(orgIdRef.current, entry).catch(handleSyncError('создать audit entry'));
  }, []);

  return {
    syncCreateAuditEntry,
    // Operations
    syncCreateOperation, syncUpdateOperation, syncDeleteOperation, syncUndoDebtPayment,
    // Accounts + Чанк J (2026-05-19)
    syncCreateAccount, syncUpdateAccount, syncDeleteAccount, syncArchiveAccountWithTransfer,
    // Debts
    syncCreateDebt, syncUpdateDebt, syncDeleteDebt,
    // Contractors
    syncCreateContractor, syncUpdateContractor, syncDeleteContractor,
    // Funds (20a.4c.1) + Чанк I (2026-05-19)
    syncCreateFund, syncUpdateFund, syncArchiveFund, syncArchiveFundWithRedistribute,
    // Categories (Stage 20b B.3)
    syncCreateCategory, syncUpdateCategory, syncArchiveCategory,
    // Users
    syncAddUser, syncUpdateUserRole, syncRemoveUser,
  };
}
